/**
 * Settings context for application-wide configuration
 */

import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export interface ColorScheme {
  lineOdd: string;
  lineEven: string;
  background: string;
  text: string;
}

export interface Settings {
  alternatingLines: boolean;
  animationEnabled: boolean;
  customColors: ColorScheme;
  portraitMode: boolean; // Rotate entire UI 90° for portrait/vertical viewing
  fontFamily: string;
  fontSize: number;
  horizontalScroll: boolean;
  autoscaleLines: boolean;
  maxLines: number; // 0 means show all lines
  modelFilter: string; // Empty string means "all models"
}

export const FONT_OPTIONS = [
  { value: "'Monaco', 'Menlo', monospace", label: "Monaco (Default)" },
  { value: "'Fira Code', 'Fira Mono', monospace", label: "Fira Code" },
  { value: "'JetBrains Mono', monospace", label: "JetBrains Mono" },
  { value: "'Source Code Pro', monospace", label: "Source Code Pro" },
  { value: "'IBM Plex Mono', monospace", label: "IBM Plex Mono" },
  { value: "'Consolas', 'Courier New', monospace", label: "Consolas" },
  { value: "'SF Mono', 'Menlo', monospace", label: "SF Mono" },
];

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  resetSettings: () => void;
}

const DEFAULT_COLORS: ColorScheme = {
  lineOdd: '',
  lineEven: '',
  background: '',
  text: '',
};

// Detect mobile for default font size
const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

const DEFAULT_SETTINGS: Settings = {
  alternatingLines: false,
  animationEnabled: true,
  customColors: DEFAULT_COLORS,
  portraitMode: false,
  fontFamily: "'Monaco', 'Menlo', monospace",
  fontSize: isMobile ? 6 : 14,
  horizontalScroll: false,
  autoscaleLines: false,
  maxLines: 0, // 0 = show all
  modelFilter: '', // Empty = show all models
};

const STORAGE_KEY = 'cascades-settings';

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    // Load settings from localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<Settings>;
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
    return DEFAULT_SETTINGS;
  });

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }, [settings]);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};
